/**
 * RichTextEditor — WYSIWYG email composer powered by TipTap.
 *
 * Two modes:
 *   • "wysiwyg" (default) — rich toolbar, token chips, what-you-see-is-what-you-get
 *   • "html"              — raw HTML escape hatch for power users
 *
 * Public surface: <RichTextEditor html onChange placeholder/>.
 * Outputs email-friendly HTML with inline-able styles (we keep style attrs
 * on links/headings; the campaign send pipeline runs Juice on it later).
 */
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { resolveBackendFileUrl } from "@/lib/fileUrls";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { Link } from "@tiptap/extension-link";
import { Image } from "@tiptap/extension-image";
import { TextStyle, FontFamily } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Placeholder } from "@tiptap/extension-placeholder";

/**
 * TipTap's stock `Image` extension only models `src`, `alt`, `title` —
 * any inline `style` we emit from the Assets picker (e.g. the explicit
 * `height:55px;max-width:180px` for a Jan Harris signature) is silently
 * dropped on parse, and the image renders at its intrinsic 600×183 size
 * which dominates the email body. Same for `width` / `height` HTML attrs
 * that some legacy emails carry.
 *
 * We extend the schema so those three attributes ride through every
 * round-trip (insert → serialize → reload → serialize), which is the
 * only reliable place to fix this — a post-render normalizer can't help
 * because every subsequent `setContent` re-strips the attribute again.
 */
const SizedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute("style"),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute("width"),
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute("height"),
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {}),
      },
    };
  },
});
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  ListBullets, ListNumbers, Link as LinkIcon, Image as ImageIcon,
  Minus, ArrowUUpLeft, ArrowUUpRight, Code, TextAlignLeft,
  TextAlignCenter, TextAlignRight, TextHOne, TextHTwo, TextHThree,
  Sparkle, TextT, Package, StackSimple,
} from "@phosphor-icons/react";

// Available merge tokens. Keep this list in sync with the backend renderer.
const TOKENS = [
  { token: "{{FirstName}}", label: "First name" },
  { token: "{{LastName}}", label: "Last name" },
  { token: "{{FullName}}", label: "Full name" },
  { token: "{{Company}}", label: "Company" },
  { token: "{{CalendarLink}}", label: "Calendar link (auto-link)" },
  { token: "{{UnsubscribeURL}}", label: "Unsubscribe URL" },
  { token: "{{BrowserURL}}", label: "View-in-browser URL" },
];

// Email-safe fonts — every major mail client falls back gracefully if it
// doesn't have these. Order matches a recommended UX (most common first).
const FONTS = [
  { label: "Default (Arial)", value: "" },
  { label: "Arial", value: "Arial, Helvetica, sans-serif" },
  { label: "Helvetica", value: "Helvetica, Arial, sans-serif" },
  { label: "Verdana", value: "Verdana, Geneva, sans-serif" },
  { label: "Tahoma", value: "Tahoma, Geneva, sans-serif" },
  { label: "Trebuchet MS", value: "'Trebuchet MS', sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
  { label: "Courier New", value: "'Courier New', Courier, monospace" },
  { label: "Lucida Sans", value: "'Lucida Sans Unicode', 'Lucida Grande', sans-serif" },
];

// Google Workspace-style colour palette. Top row is greys (white → black);
// the next row is base hues; everything below is tints (lighter) and shades
// (darker) of the same hues. We keep it as a flat 2-D matrix [row][col]
// rather than a flat list because the picker renders it as a grid where
// vertical neighbours are the same hue family — picking a darker red means
// scanning DOWN, not hunting around. This mirrors what marketers see in
// Gmail/Docs and what the user explicitly asked for.
const GOOGLE_PALETTE = [
  // Greys: white → black with 5 mid stops + 1 light/dark
  ["#FFFFFF", "#F3F3F3", "#CCCCCC", "#999999", "#666666", "#434343", "#222222", "#000000"],
  // Saturated base hues
  ["#FFEBEE", "#FFF3E0", "#FFFDE7", "#E8F5E9", "#E0F7FA", "#E3F2FD", "#EDE7F6", "#FCE4EC"],
  ["#FFCDD2", "#FFE0B2", "#FFF59D", "#C8E6C9", "#B2EBF2", "#BBDEFB", "#D1C4E9", "#F8BBD0"],
  ["#EF9A9A", "#FFB74D", "#FFEE58", "#A5D6A7", "#80DEEA", "#90CAF9", "#B39DDB", "#F48FB1"],
  ["#E53935", "#FB8C00", "#FDD835", "#43A047", "#00ACC1", "#1E88E5", "#5E35B1", "#D81B60"],
  ["#B71C1C", "#E65100", "#F9A825", "#1B5E20", "#006064", "#0D47A1", "#311B92", "#880E4F"],
];

function ToolbarButton({ onClick, active, disabled, children, title, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`h-7 w-7 flex items-center justify-center text-[13px] border border-transparent transition-colors ${
        active
          ? "bg-[#0055FF] text-white"
          : "text-[#525252] hover:bg-[#F5F5F5] hover:border-[#E5E5E5]"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="w-px h-5 bg-[#E5E5E5] mx-1" />;
}

export default function RichTextEditor({
  html,
  onChange,
  placeholder = "Write your email…",
  minHeight = 320,
  testId = "rich-text-editor",
}) {
  const [mode, setMode] = useState("wysiwyg"); // "wysiwyg" | "html"
  const [showTokens, setShowTokens] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [showSnippets, setShowSnippets] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [snippets, setSnippets] = useState([]);
  const [snippetsLoaded, setSnippetsLoaded] = useState(false);
  const tokensRef = useRef(null);
  const colorsRef = useRef(null);
  const assetsRef = useRef(null);
  const snippetsRef = useRef(null);
  const lastEmittedHtml = useRef("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      Color,
      Highlight.configure({ multicolor: true }),
      Link.configure({
        openOnClick: false,
        // Auto-wrap typed/pasted URLs in <a> as the user authors. Without
        // this, marketers who paste a Zoom scheduler link inline get a
        // plain-text URL in the rendered email — even though it looks
        // like a link in the WYSIWYG. The server-side post-processor
        // also auto-links as a safety net for legacy / programmatically-
        // generated content.
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank", style: "color:#0055FF;text-decoration:underline" },
      }),
      // SizedImage preserves the inline `style` attribute we emit when
      // marketers pick a brand asset. The default HTMLAttributes.style is
      // kept as a fallback for plain `<img>` tags that ship without one.
      SizedImage.configure({ HTMLAttributes: { style: "max-width:100%;height:auto" } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
    ],
    content: html || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-4 py-3",
        style: `min-height:${minHeight}px;font-family:Arial,Helvetica,sans-serif;color:#171717;line-height:1.55`,
      },
    },
    onUpdate: ({ editor }) => {
      const next = editor.getHTML();
      lastEmittedHtml.current = next;
      onChange?.(next);
    },
  });

  // Keep editor synced when parent rewrites content (e.g. switching steps).
  useEffect(() => {
    if (!editor) return;
    if (html !== lastEmittedHtml.current && html !== editor.getHTML()) {
      editor.commands.setContent(html || "", { emitUpdate: false });
      lastEmittedHtml.current = html || "";
    }
  }, [html, editor]);

  // Retro-normalize previously-inserted brand-asset images whose inline styles
  // predate the Session-53 sizing fix. Without this, a signature embedded a
  // week ago keeps the old `max-width:100%;height:auto` (renders at intrinsic
  // size — huge) even after the code change. We run once per content load.
  useEffect(() => {
    if (!editor || !assets || assets.length === 0) return;
    const sizeByType = {
      signature: "display:inline-block;height:55px;width:auto;max-width:180px;vertical-align:middle",
      logo: "display:inline-block;height:47px;width:auto;vertical-align:middle",
      social: "display:inline-block;width:28px;height:28px;vertical-align:middle",
      icon: "display:inline-block;width:28px;height:28px;vertical-align:middle",
    };
    const byId = {};
    assets.forEach(a => { byId[a.id] = a; });
    let changed = false;
    let current = editor.getHTML();
    const next = current.replace(/<img[^>]*>/g, (tag) => {
      // Find which asset this img belongs to (asset id appears in the URL).
      const match = Object.keys(byId).find(id => tag.includes(id));
      if (!match) return tag;
      const want = sizeByType[byId[match].asset_type];
      if (!want) return tag;
      // Skip if already normalized (has height declaration).
      if (/height\s*:\s*\d+px/.test(tag) && tag.includes(want.split(";")[1].trim())) return tag;
      changed = true;
      let newTag = tag.replace(/\s+width="[^"]*"/g, "").replace(/\s+height="[^"]*"/g, "");
      if (/style="[^"]*"/.test(newTag)) {
        newTag = newTag.replace(/style="[^"]*"/, `style="${want}"`);
      } else {
        newTag = newTag.replace(/<img/, `<img style="${want}"`);
      }
      return newTag;
    });
    if (changed && next !== current) {
      editor.commands.setContent(next, { emitUpdate: false });
      lastEmittedHtml.current = next;
      onChange?.(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, assets]);

  // Close the token / color / assets / snippets popovers on outside click.
  useEffect(() => {
    if (!showTokens && !showColors && !showAssets && !showSnippets) return;
    const onDocClick = (e) => {
      if (showTokens && tokensRef.current && !tokensRef.current.contains(e.target)) setShowTokens(false);
      if (showColors && colorsRef.current && !colorsRef.current.contains(e.target)) setShowColors(false);
      if (showAssets && assetsRef.current && !assetsRef.current.contains(e.target)) setShowAssets(false);
      if (showSnippets && snippetsRef.current && !snippetsRef.current.contains(e.target)) setShowSnippets(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showTokens, showColors, showAssets, showSnippets]);

  // Eager-load brand assets so the retro-normalization effect above can run
  // for already-inserted signatures without the user having to open the
  // Assets picker first.
  useEffect(() => {
    if (assetsLoaded) return;
    (async () => {
      try {
        const r = await api.get("/marketing/brand-assets");
        setAssets(r.data || []);
      } catch { /* ignore */ }
      setAssetsLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lazy-load reusable content-block snippets (sig, footer, CTA, …).
  useEffect(() => {
    if (!showSnippets || snippetsLoaded) return;
    (async () => {
      try {
        const r = await api.get("/marketing/content-blocks");
        setSnippets((r.data?.items || r.data || []));
      } catch { /* ignore */ }
      setSnippetsLoaded(true);
    })();
  }, [showSnippets, snippetsLoaded]);

  if (!editor) return null;

  const insertToken = (token) => {
    editor.chain().focus().insertContent(token).run();
    setShowTokens(false);
  };

  const promptLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL (leave blank to remove):", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  const promptImage = () => {
    const url = window.prompt("Image URL:", "https://");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const insertAsset = (a) => {
    // Build an email-safe <a><img/></a> snippet. Explicit pixel sizes per
    // asset type — critical because Outlook/Gmail ignore `max-width` and
    // would otherwise render high-resolution signatures at their intrinsic
    // size (Jan Harris's 1800px signature was dominating the email body).
    const alt = (a.alt_text || a.name || "").replace(/"/g, "&quot;");
    const src = resolveBackendFileUrl(a.image_url);
    let sizeCss;
    if (a.asset_type === "social" || a.asset_type === "icon") {
      sizeCss = "width:28px;height:28px;";
    } else if (a.asset_type === "signature") {
      // Sits comfortably beside a name+title block without dominating the
      // email body. ~55px tall is roughly 30% of the signatures' intrinsic
      // height (600×183 in storage) — close to how a real handwritten sign-off
      // looks on a printed letter.
      sizeCss = "height:55px;width:auto;max-width:180px;";
    } else if (a.asset_type === "logo") {
      // Eyvo logo — 47px is what the email-template header + proposal PDF use.
      sizeCss = "height:47px;width:auto;";
    } else {
      sizeCss = "max-width:100%;height:auto;";
    }
    const img = `<img src="${src}" alt="${alt}" style="display:inline-block;${sizeCss}vertical-align:middle" />`;
    const html = a.click_url
      ? `<a href="${a.click_url}" style="display:inline-block">${img}</a>`
      : img;
    editor.chain().focus().insertContent(html + "&nbsp;").run();
    setShowAssets(false);
  };

  const insertSnippet = async (s) => {
    // The list endpoint omits body_html to keep payloads small; fetch the
    // full record before inserting so we get the real HTML (and, for
    // header/navigation blocks, a descriptive label for the chip).
    try {
      setShowSnippets(false);

      // Header / navigation blocks use <table> layouts that Tiptap's
      // schema would strip — leaving 4 stacked <a> tags where a 4-column
      // row should be. For those, insert a plain-text token instead; the
      // server-side postprocess_email_html expands it back to the real
      // HTML right before the email ships. Tiptap preserves plain text
      // losslessly and never touches our existing paragraph structure.
      const cat = (s.category || "").toLowerCase();
      if (cat === "header" || cat === "navigation") {
        const token = `{{SNIPPET:${s.id}}}`;
        // insertContent with a fragment that's wrapped in its own
        // paragraph avoids merging the token into the previous line —
        // which is what was eating the start of the user's first
        // sentence before. The leading <p><br/></p> makes sure the
        // cursor ends up on a fresh line below the token.
        editor
          .chain()
          .focus()
          .insertContent(`<p>${token}</p><p><br/></p>`)
          .run();
        return;
      }

      let html = s.body_html || s.html || "";
      if (!html) {
        const r = await api.get(`/marketing/content-blocks/${s.id}`);
        html = r.data?.body_html || "";
      }
      if (!html) return;
      editor.chain().focus().insertContent(html).run();
    } catch (err) {
      console.error("Failed to load snippet", err);
    }
  };

  const insertDivider = () => {
    editor.chain().focus().insertContent("<hr/>").run();
  };

  return (
    <div className="border border-[#E5E5E5] bg-white" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex items-center flex-wrap gap-0.5 border-b border-[#E5E5E5] bg-[#FAFAFA] px-2 py-1.5">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()}
                       active={editor.isActive("bold")} title="Bold (⌘B)" testId="rt-bold">
          <TextB size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()}
                       active={editor.isActive("italic")} title="Italic (⌘I)" testId="rt-italic">
          <TextItalic size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()}
                       active={editor.isActive("underline")} title="Underline (⌘U)" testId="rt-underline">
          <TextUnderline size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()}
                       active={editor.isActive("strike")} title="Strikethrough" testId="rt-strike">
          <TextStrikethrough size={14} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                       active={editor.isActive("heading", { level: 1 })} title="Heading 1" testId="rt-h1">
          <TextHOne size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                       active={editor.isActive("heading", { level: 2 })} title="Heading 2" testId="rt-h2">
          <TextHTwo size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                       active={editor.isActive("heading", { level: 3 })} title="Heading 3" testId="rt-h3">
          <TextHThree size={14} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()}
                       active={editor.isActive("bulletList")} title="Bulleted list" testId="rt-bullet">
          <ListBullets size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()}
                       active={editor.isActive("orderedList")} title="Numbered list" testId="rt-ordered">
          <ListNumbers size={14} weight="bold" />
        </ToolbarButton>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("left").run()}
                       active={editor.isActive({ textAlign: "left" })} title="Align left" testId="rt-align-left">
          <TextAlignLeft size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("center").run()}
                       active={editor.isActive({ textAlign: "center" })} title="Align center" testId="rt-align-center">
          <TextAlignCenter size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign("right").run()}
                       active={editor.isActive({ textAlign: "right" })} title="Align right" testId="rt-align-right">
          <TextAlignRight size={14} weight="bold" />
        </ToolbarButton>

        <Divider />

        {/* Font family dropdown */}
        <select
          value={editor.getAttributes("textStyle").fontFamily || ""}
          onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => {
            const v = e.target.value;
            if (v) editor.chain().focus().setFontFamily(v).run();
            else editor.chain().focus().unsetFontFamily().run();
          }}
          title="Font family"
          data-testid="rt-font-family"
          className="h-7 px-1.5 text-xs border border-[#E5E5E5] bg-white hover:border-[#0055FF] focus:outline-none focus:border-[#0055FF] cursor-pointer"
          style={{ fontFamily: editor.getAttributes("textStyle").fontFamily || "inherit" }}
        >
          {FONTS.map(f => (
            <option key={f.value} value={f.value} style={{ fontFamily: f.value || "inherit" }}>
              {f.label}
            </option>
          ))}
        </select>

        {/* Color picker — Google Workspace-style 2-section palette
             (Text colour | Highlight colour). Same matrix for both, just
             different commands. We render a 8×6 grid where rows form
             tint→shade gradients of the same hue, mirroring Gmail/Docs
             muscle-memory. The "Reset" entries clear the property entirely
             rather than setting it to a default colour, which keeps the
             default theme in control of inherited styling. */}
        <div className="relative" ref={colorsRef}>
          <button
            type="button"
            onClick={() => setShowColors(v => !v)}
            className="h-7 w-7 flex flex-col items-center justify-center text-[#525252] hover:bg-[#F5F5F5] border border-transparent hover:border-[#E5E5E5]"
            title="Text & highlight colour"
            data-testid="rt-color"
          >
            <TextT size={12} weight="bold" />
            <span className="block w-3.5 h-0.5 mt-0.5" style={{ backgroundColor: editor.getAttributes("textStyle").color || "#0A0A0A" }} />
          </button>
          {showColors && (
            <div className="absolute z-30 left-0 top-9 w-[28rem] bg-white border border-[#E5E5E5] shadow-lg p-3" data-testid="rt-color-popover">
              <div className="grid grid-cols-2 gap-3">
                <ColorPaletteSection
                  label="Text"
                  paletteId="text"
                  current={editor.getAttributes("textStyle").color}
                  onPick={(c) => { editor.chain().focus().setColor(c).run(); setShowColors(false); }}
                  onReset={() => { editor.chain().focus().unsetColor().run(); setShowColors(false); }}
                />
                <ColorPaletteSection
                  label="Highlight"
                  paletteId="highlight"
                  current={editor.getAttributes("highlight").color}
                  onPick={(c) => { editor.chain().focus().toggleHighlight({ color: c }).run(); setShowColors(false); }}
                  onReset={() => { editor.chain().focus().unsetHighlight().run(); setShowColors(false); }}
                />
              </div>
            </div>
          )}
        </div>

        <Divider />

        <ToolbarButton onClick={promptLink} active={editor.isActive("link")} title="Insert / edit link" testId="rt-link">
          <LinkIcon size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={promptImage} title="Insert image by URL" testId="rt-image">
          <ImageIcon size={14} weight="bold" />
        </ToolbarButton>

        <ToolbarButton onClick={insertDivider} title="Insert horizontal divider" testId="rt-hr">
          <Minus size={14} weight="bold" />
        </ToolbarButton>

        {/* Reusable content snippets — full signature, footers, CTAs, etc. */}
        <div className="relative" ref={snippetsRef}>
          <button
            type="button"
            onClick={() => setShowSnippets(v => !v)}
            className="h-7 px-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#16A34A] hover:bg-[#F0FDF4] border border-[#BBF7D0]"
            title="Insert reusable snippet (signature, footer, CTA)"
            data-testid="rt-snippets-menu"
          >
            <StackSimple size={12} weight="bold" /> Snippets
          </button>
          {showSnippets && (
            <div className="absolute z-30 left-0 top-9 w-80 bg-white border border-[#E5E5E5] shadow-lg" data-testid="rt-snippets-popover">
              <div className="px-3 py-2 border-b border-[#F5F5F5] flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#525252]">Content snippets</p>
                <a href="/marketing/content-blocks" target="_blank" rel="noopener noreferrer"
                   className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline">
                  Manage →
                </a>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {!snippetsLoaded ? (
                  <div className="p-4 text-center text-[11px] text-[#A3A3A3]">Loading…</div>
                ) : snippets.length === 0 ? (
                  <div className="p-4 text-center text-[11px] text-[#A3A3A3]">
                    No snippets yet. Create one in Content Blocks.
                  </div>
                ) : (
                  <div className="p-1">
                    {snippets.map(s => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => insertSnippet(s)}
                        className="w-full text-left px-3 py-2 hover:bg-[#FAFAFA] border-b border-[#F5F5F5] last:border-b-0"
                        title={s.description || s.name}
                        data-testid={`rt-snippet-${s.id}`}
                      >
                        <p className="text-xs font-bold text-[#0A0A0A] truncate">{s.name}</p>
                        {s.category && (
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#16A34A] mt-0.5">{s.category}</p>
                        )}
                        {s.description && (
                          <p className="text-[10px] text-[#525252] mt-1 line-clamp-2">{s.description}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Brand asset library — logos, signatures, social icons. Positioned
            between Snippets and Variable per the marketing team's layout
            request, and the popover opens toward the LEFT (right-0) so it
            doesn't clip off the right edge of the toolbar container. */}
        <div className="relative" ref={assetsRef}>
          <button
            type="button"
            onClick={() => setShowAssets(v => !v)}
            className="h-7 px-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#0055FF] hover:bg-[#EFF6FF] border border-[#DBEAFE]"
            title="Insert brand asset (logo, signature, social icon)"
            data-testid="rt-assets-menu"
          >
            <Package size={12} weight="bold" /> Assets
          </button>
          {showAssets && (
            <div className="absolute z-30 right-0 top-9 w-[22rem] bg-white border border-[#E5E5E5] shadow-lg" data-testid="rt-assets-popover">
              <div className="px-3 py-2 border-b border-[#F5F5F5] flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#525252]">Brand assets</p>
                <a href="/marketing/brand-assets" target="_blank" rel="noopener noreferrer"
                   className="text-[10px] font-bold uppercase tracking-wider text-[#0055FF] hover:underline">
                  Manage →
                </a>
              </div>
              <div className="max-h-[28rem] overflow-y-auto">
                {!assetsLoaded ? (
                  <div className="p-4 text-center text-[11px] text-[#A3A3A3]">Loading…</div>
                ) : assets.length === 0 ? (
                  <div className="p-4 text-center text-[11px] text-[#A3A3A3]">
                    <p>No brand assets yet.</p>
                    <a href="/marketing/brand-assets" target="_blank" rel="noopener noreferrer"
                       className="text-[11px] font-bold text-[#0055FF] hover:underline mt-1 inline-block">
                      Upload your first →
                    </a>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-1 p-2">
                    {assets.map(a => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => insertAsset(a)}
                        className="group bg-[#FAFAFA] hover:bg-white hover:border-[#0055FF] border border-transparent p-2 flex flex-col items-center gap-1"
                        title={`${a.name}${a.click_url ? `\n→ ${a.click_url}` : ""}`}
                        data-testid={`rt-asset-${a.id}`}
                      >
                        <div className="w-full h-14 flex items-center justify-center overflow-hidden">
                          <img src={resolveBackendFileUrl(a.image_url)} alt={a.alt_text || a.name}
                               className="max-w-full max-h-full object-contain" />
                        </div>
                        <p className="text-[9px] font-semibold text-[#525252] truncate w-full text-center">{a.name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Token chip dropdown */}
        <div className="relative" ref={tokensRef}>
          <button
            type="button"
            onClick={() => setShowTokens(v => !v)}
            className="h-7 px-2 flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#7C3AED] hover:bg-[#F5F3FF] border border-[#DDD6FE]"
            title="Insert merge variable"
            data-testid="rt-token-menu"
          >
            <Sparkle size={12} weight="fill" /> Variable
          </button>
          {showTokens && (
            <div className="absolute z-30 left-0 top-9 w-64 bg-white border border-[#E5E5E5] shadow-lg py-1" data-testid="rt-token-list">
              {TOKENS.map(t => (
                <button
                  key={t.token}
                  type="button"
                  onClick={() => insertToken(t.token)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-[#FAFAFA]"
                  data-testid={`rt-token-${t.token}`}
                >
                  <span className="font-mono text-[#7C3AED]">{t.token}</span>
                  <span className="text-[#A3A3A3] text-[10px]">{t.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Divider />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()}
                       disabled={!editor.can().undo()} title="Undo (⌘Z)" testId="rt-undo">
          <ArrowUUpLeft size={14} weight="bold" />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()}
                       disabled={!editor.can().redo()} title="Redo (⌘⇧Z)" testId="rt-redo">
          <ArrowUUpRight size={14} weight="bold" />
        </ToolbarButton>

        <div className="flex-1" />

        {/* Mode toggle */}
        <div className="flex border border-[#E5E5E5]">
          <button
            type="button"
            onClick={() => setMode("wysiwyg")}
            className={`h-7 px-2 text-[10px] font-bold uppercase tracking-wider ${mode === "wysiwyg" ? "bg-[#0055FF] text-white" : "bg-white text-[#525252] hover:bg-[#F5F5F5]"}`}
            data-testid="rt-mode-wysiwyg"
          >
            Visual
          </button>
          <button
            type="button"
            onClick={() => setMode("html")}
            className={`h-7 px-2 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${mode === "html" ? "bg-[#0055FF] text-white" : "bg-white text-[#525252] hover:bg-[#F5F5F5]"}`}
            data-testid="rt-mode-html"
          >
            <Code size={11} weight="bold" /> HTML
          </button>
        </div>
      </div>

      {/* Editor body */}
      {mode === "wysiwyg" ? (
        <EditorContent editor={editor} data-testid="rt-canvas" />
      ) : (
        <textarea
          value={editor.getHTML()}
          onChange={(e) => {
            const next = e.target.value;
            lastEmittedHtml.current = next;
            editor.commands.setContent(next, { emitUpdate: false });
            onChange?.(next);
          }}
          rows={Math.max(14, Math.floor(minHeight / 22))}
          spellCheck={false}
          className="w-full px-3 py-2 text-xs font-mono border-0 focus:outline-none"
          style={{ minHeight: `${minHeight}px` }}
          data-testid="rt-html-textarea"
        />
      )}
    </div>
  );
}



/**
 * One half of the colour popover — renders the Google palette as an 8-wide
 * grid with the currently-applied colour outlined. `onPick` and `onReset`
 * let the parent wire up text vs highlight commands without duplicating
 * the layout. Keeping this as a sibling component (not extracted to its
 * own file) until a third caller appears.
 */
function ColorPaletteSection({ label, paletteId, current, onPick, onReset }) {
  return (
    <div data-testid={`rt-palette-${paletteId}`}>
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3] mb-1.5">
        {label}
      </p>
      <div className="grid grid-cols-8 gap-0.5" role="grid">
        {GOOGLE_PALETTE.map((row, rIdx) => row.map((c) => {
          const selected = current && current.toLowerCase() === c.toLowerCase();
          return (
            <button
              key={`${rIdx}-${c}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(c)}
              title={c}
              data-testid={`rt-color-${paletteId}-${c.replace("#", "").toLowerCase()}`}
              className={`w-5 h-5 border transition-transform hover:scale-110 ${
                selected
                  ? "border-[#0055FF] ring-1 ring-[#0055FF]"
                  : "border-[#E5E5E5]"
              }`}
              style={{ backgroundColor: c }}
            />
          );
        }))}
      </div>
      <div className="mt-2 pt-2 border-t border-[#F5F5F5] flex items-center gap-2">
        <label className="flex-1 flex items-center gap-1.5 cursor-pointer hover:bg-[#FAFAFA] px-1.5 py-1 -mx-1.5">
          <span
            className="w-4 h-4 border border-[#E5E5E5] flex-shrink-0"
            style={{ backgroundColor: current || "#0A0A0A" }}
            aria-hidden
          />
          <span className="text-[10px] font-semibold text-[#525252] flex-1">Custom…</span>
          <input
            type="color"
            value={current || "#0A0A0A"}
            onChange={(e) => onPick(e.target.value)}
            className="sr-only"
            data-testid={`rt-color-${paletteId}-custom`}
          />
        </label>
        <button
          type="button"
          onClick={onReset}
          className="text-[10px] font-semibold text-[#525252] hover:text-[#DC2626] px-1.5 py-1"
          data-testid={`rt-color-${paletteId}-reset`}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
